import { AgentActionsPanelHeader } from "@app/components/assistant/conversation/actions/AgentActionsPanelHeader";
import type { AgentMessageWithStreaming } from "@app/components/assistant/conversation/types";
import {
  formatRunDuration,
  formatTokenCount,
} from "@app/components/assistant/conversation/WorkReceiptPopover";
import { InternalActionIcons } from "@app/components/resources/resources_icons";
import { useConversationMessage } from "@app/hooks/conversations";
import { useWorkReceipt } from "@app/hooks/conversations/useWorkReceipt";
import { getActionOneLineLabel } from "@app/lib/api/assistant/activity_steps";
import { agentAvatarVisual } from "@app/types/assistant/avatar";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import { isAgentMessageType } from "@app/types/assistant/conversation";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Avatar,
  CheckCircle,
  ChevronRight,
  cn,
  Icon,
  Markdown,
  Spinner,
  XClose,
} from "@dust-tt/sparkle";
import { useMemo } from "react";

interface AgentRunDetailsPanelProps {
  conversation: ConversationWithoutContentType;
  owner: LightWorkspaceType;
  messageId: string;
  virtuosoMsg: AgentMessageWithStreaming | null;
  onClose: () => void;
  onSelectAction: (actionId: string) => void;
}

export function AgentRunDetailsPanel({
  conversation,
  owner,
  messageId,
  virtuosoMsg,
  onClose,
  onSelectAction,
}: AgentRunDetailsPanelProps) {
  const { message: fetchedMessage, isMessageLoading } = useConversationMessage({
    conversationId: conversation.sId,
    workspaceId: owner.sId,
    messageId,
  });

  const { receipt, isReceiptLoading } = useWorkReceipt({
    conversationId: conversation.sId,
    workspaceId: owner.sId,
    messageId,
    disabled: false,
  });

  const agentMessage = useMemo(() => {
    if (virtuosoMsg?.sId === messageId) {
      return virtuosoMsg;
    }
    if (fetchedMessage && isAgentMessageType(fetchedMessage)) {
      return fetchedMessage;
    }
    return null;
  }, [virtuosoMsg, fetchedMessage, messageId]);

  const agentConfig = agentMessage?.configuration;

  const thinkingContent = useMemo(() => {
    const rawCoT =
      (virtuosoMsg?.sId === messageId
        ? virtuosoMsg.chainOfThought
        : agentMessage?.chainOfThought) ?? "";

    const discreteSteps = (
      virtuosoMsg?.sId === messageId
        ? virtuosoMsg.streaming.inlineActivitySteps
        : agentMessage &&
            "activitySteps" in agentMessage &&
            agentMessage.activitySteps
          ? agentMessage.activitySteps
          : []
    )
      ?.filter(
        (s): s is Extract<typeof s, { type: "thinking" }> =>
          s.type === "thinking" && Boolean(s.content?.trim())
      )
      .map((s) => s.content.trim());

    if (rawCoT.trim()) {
      return rawCoT.trim();
    }
    if (discreteSteps && discreteSteps.length > 0) {
      return discreteSteps.join("\n\n---\n\n");
    }
    return null;
  }, [virtuosoMsg, agentMessage, messageId]);

  const toolItems = useMemo(() => {
    if (receipt?.toolCalls && receipt.toolCalls.length > 0) {
      return receipt.toolCalls.map((tc, index) => ({
        id: tc.actionId ?? `${tc.toolName}-${tc.executedAt}-${index}`,
        actionId: tc.actionId,
        label: tc.label ?? tc.toolName,
        toolName: tc.toolName,
        isClickable: Boolean(tc.actionId),
      }));
    }

    const liveActions =
      virtuosoMsg?.sId === messageId
        ? virtuosoMsg.actions
        : agentMessage &&
            "actions" in agentMessage &&
            Array.isArray(agentMessage.actions)
          ? agentMessage.actions
          : [];

    return liveActions.map((a) => ({
      id: a.sId,
      actionId: a.sId,
      label: getActionOneLineLabel(a),
      toolName: a.toolName,
      isClickable: true,
    }));
  }, [receipt, virtuosoMsg, agentMessage, messageId]);

  const durationMs =
    receipt?.durationMs ??
    agentMessage?.completionDurationMs ??
    (agentMessage?.completedTs && agentMessage.created
      ? agentMessage.completedTs - agentMessage.created
      : null);

  const tokens = receipt?.tokens ?? null;

  if (isMessageLoading && !agentMessage && !receipt) {
    return (
      <div className="flex h-panel flex-col bg-panel-background">
        <AgentActionsPanelHeader
          title="Run details"
          closeIcon={XClose}
          onClose={onClose}
        />
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      </div>
    );
  }

  const avatarUrl = agentConfig
    ? agentAvatarVisual(agentConfig)
    : undefined;

  return (
    <div className="flex h-panel flex-col bg-panel-background">
      <AgentActionsPanelHeader
        title="Run details"
        closeIcon={XClose}
        onClose={onClose}
      />
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Agent & Run Overview */}
        <div className="rounded-xl border border-border/80 bg-muted/20 p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar
                visual={avatarUrl}
                name={agentConfig?.name ?? "Agent"}
                size="sm"
              />
              <div className="min-w-0">
                <div className="font-semibold text-sm text-foreground truncate">
                  {agentConfig ? `@${agentConfig.name}` : "Agent Run"}
                </div>
                {agentMessage?.resolvedModel && (
                  <div className="text-xs text-muted-foreground truncate">
                    {agentMessage.resolvedModel.modelId}
                  </div>
                )}
              </div>
            </div>

            {agentMessage?.status && (
              <span
                className={cn(
                  "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
                  agentMessage.status === "created"
                    ? "bg-blue-500/10 text-blue-500"
                    : agentMessage.status === "failed"
                      ? "bg-red-500/10 text-red-500"
                      : agentMessage.status === "cancelled"
                        ? "bg-amber-500/10 text-amber-500"
                        : "bg-green-500/10 text-green-600 dark:text-green-400"
                )}
              >
                {agentMessage.status === "created"
                  ? "Running"
                  : agentMessage.status === "failed"
                    ? "Errored"
                    : agentMessage.status === "cancelled"
                      ? "Cancelled"
                      : "Completed"}
              </span>
            )}
          </div>

          {receipt?.requestedBy && (
            <div className="flex items-center gap-1.5 rounded-lg bg-muted-background px-2.5 py-1.5 text-xs text-muted-foreground">
              <Icon
                visual={InternalActionIcons.ActionRobotIcon}
                size="xs"
                className="shrink-0"
              />
              <span>
                Delegated by another agent — this answer is attributed to its
                author.
              </span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground pt-1 border-t border-border/40">
            {durationMs !== null && (
              <span>Ran for {formatRunDuration(durationMs)}</span>
            )}
            {tokens !== null && <span>· {formatTokenCount(tokens)}</span>}
          </div>
        </div>

        {/* Thinking & Reasoning */}
        {thinkingContent && (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Icon
                visual={InternalActionIcons.ActionBrainIcon}
                size="xs"
                className="text-primary/70"
              />
              <span>Thinking & Reasoning</span>
            </div>
            <div className="rounded-xl border border-border/70 bg-muted/30 p-3.5 text-sm leading-relaxed">
              <Markdown
                content={thinkingContent}
                streamingState="none"
                forcedTextSize="text-sm"
                textColor="text-foreground"
                isLastMessage={false}
              />
            </div>
          </div>
        )}

        {/* Tool Calls */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <span>Tool Calls ({toolItems.length})</span>
          </div>

          {isReceiptLoading && !receipt && toolItems.length === 0 ? (
            <div className="flex items-center gap-2 rounded-xl border border-border/60 p-3 text-sm text-muted-foreground">
              <Spinner size="xs" />
              <span>Loading tool history…</span>
            </div>
          ) : toolItems.length > 0 ? (
            <ul className="space-y-1.5">
              {toolItems.map((toolItem) => {
                const content = (
                  <>
                    <Icon
                      visual={CheckCircle}
                      size="xs"
                      className="shrink-0 text-success-500"
                    />
                    <span className="min-w-0 flex-1 truncate font-medium text-foreground text-sm">
                      {toolItem.label}
                    </span>
                    {toolItem.isClickable && (
                      <Icon
                        visual={ChevronRight}
                        size="xs"
                        className="shrink-0 opacity-50 transition-opacity group-hover:opacity-100"
                      />
                    )}
                  </>
                );

                if (!toolItem.isClickable || !toolItem.actionId) {
                  return (
                    <li
                      key={toolItem.id}
                      className="flex min-h-9 w-full items-center gap-2.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-left"
                    >
                      {content}
                    </li>
                  );
                }

                return (
                  <li key={toolItem.id}>
                    <button
                      type="button"
                      className="group flex min-h-9 w-full items-center gap-2.5 rounded-lg border border-border/50 bg-muted/20 px-3 py-2 text-left transition-colors hover:bg-muted-background hover:border-border"
                      onClick={() => onSelectAction(toolItem.actionId!)}
                    >
                      {content}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="rounded-xl border border-border/50 bg-muted/10 p-3 text-sm text-muted-foreground">
              No tools used for this message.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
